'use client';

import { CircleX } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

export interface InputOTPProps {
  /** Current code. Shorter strings leave the trailing cells empty. */
  value: string;
  onChange: (value: string) => void;
  /** Number of cells. */
  length?: number;
  /** Fires once the last cell is filled. */
  onComplete?: (value: string) => void;
  /** Error message under the cells; its presence switches to the error state. */
  error?: string;
  disabled?: boolean;
  /** Accessible name for the whole group. */
  label: string;
  autoFocus?: boolean;
  className?: string;
}

const DIGITS_ONLY = /\D/g;

/**
 * One-time code input rendered as separate cells. Each cell is its own input so
 * that mobile keyboards and password managers behave, while paste and arrow
 * navigation are handled across the whole group.
 */
const InputOTP = ({
  value,
  onChange,
  length = 6,
  onComplete,
  error,
  disabled = false,
  label,
  autoFocus = false,
  className,
}: InputOTPProps) => {
  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);
  const generatedId = React.useId();
  const errorId = `${generatedId}-error`;
  const isInvalid = Boolean(error);

  const focusCell = (index: number) => {
    const clamped = Math.min(Math.max(index, 0), length - 1);
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select();
  };

  const commit = (next: string) => {
    const trimmed = next.slice(0, length);
    onChange(trimmed);

    if (trimmed.length === length) {
      onComplete?.(trimmed);
    }
  };

  const handleChange = (index: number, raw: string) => {
    const digits = raw.replace(DIGITS_ONLY, '');

    if (!digits) {
      return;
    }

    // Typing in the middle overwrites from that cell on, and a pasted code
    // fills the remaining cells in one go.
    const next = (
      value.slice(0, index) +
      digits +
      value.slice(index + digits.length)
    ).slice(0, length);

    commit(next);
    focusCell(index + digits.length);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace') {
      event.preventDefault();

      if (value[index]) {
        commit(value.slice(0, index) + value.slice(index + 1));
        return;
      }

      commit(value.slice(0, Math.max(index - 1, 0)) + value.slice(index));
      focusCell(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCell(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCell(index + 1);
    }
  };

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {/* biome-ignore lint/a11y/useSemanticElements: a fieldset would add a
          legend box the design does not have; the group role carries the name */}
      <div
        aria-describedby={isInvalid ? errorId : undefined}
        aria-label={label}
        className='flex w-full items-center gap-3'
        role='group'
      >
        {Array.from({ length }, (_, index) => (
          <input
            aria-invalid={isInvalid || undefined}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            // biome-ignore lint/a11y/noAutofocus: the code screen exists only to
            // take this input, so focusing it saves a tap on mobile
            autoFocus={autoFocus && index === 0}
            className={cn(
              'h-14 min-w-11 max-w-12 flex-1 rounded-xl border bg-background-fill-primary text-center font-medium text-base text-text transition-colors',
              'focus:outline-hidden',
              isInvalid
                ? 'border-brand-red-300 focus:border-brand-red-300'
                : 'border-border hover:border-brand-grey2-400 focus:border-brand-green2-300',
              disabled && 'pointer-events-none opacity-50',
            )}
            disabled={disabled}
            inputMode='numeric'
            key={index}
            onChange={(event) => handleChange(index, event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => handleKeyDown(index, event)}
            ref={(element) => {
              inputsRef.current[index] = element;
            }}
            type='text'
            value={value[index] ?? ''}
          />
        ))}
      </div>

      {error && (
        <div className='flex items-start gap-1'>
          <CircleX
            aria-hidden='true'
            className='mt-0.5 size-4 shrink-0 text-text-danger'
          />
          <p
            aria-live='assertive'
            className='text-sm text-text-danger leading-[22px]'
            id={errorId}
            role='alert'
          >
            {error}
          </p>
        </div>
      )}
    </div>
  );
};
InputOTP.displayName = 'InputOTP';

export { InputOTP };
